import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { landFormSchema } from '../schema/landSchema';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import useStore from '@/lib/store';
import useLandStore from '../store/useLandStore';
import { useParams } from 'react-router-dom';
import { ContourItem, TLandContourDetail } from '../models/ILands';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { createColumns } from '../components/single/tables/columns';
import { createSelectedColumns } from '../components/single/tables/SelectedColumn';

type LandFormValues = {
  crop: string;
  value: TLandContourDetail[];
};

export default function useLandHook() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const { id } = useParams();

  const { plants } = useStore();
  const { landById, landByIdLoading, fetchLandById, fetchLandProjects } =
    useLandStore();
  useEffect(() => {
    if (id) {
      fetchLandById({ id });
    }
  }, [id]);
  const options = useMemo(() => {
    return plants?.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
  }, [plants]);

  const form = useForm<LandFormValues>({
    resolver: zodResolver(landFormSchema),
    defaultValues: {
      crop: '',
      value: [
        {
          selected: false,
        },
      ],
    },
  });

  const columns = createColumns();
  const selectedColumns = createSelectedColumns({ form, setTab });
  const { fields } = useFieldArray({
    control: form.control,
    name: 'value',
  });

  const watchedValue = useWatch({
    control: form.control,
    name: 'value',
  }) as ContourItem[];
  const crop = useWatch({ control: form.control, name: 'crop' });

  const filteredData = useMemo(() => {
    if (tab === 'all') return watchedValue;
    return watchedValue?.filter((el) => {
      if (tab === 'merged' && el.merged) return el;
      else if (tab === 'unMerged' && !el.merged && !el.selected) return el;
    });
  }, [watchedValue, tab]);

  const selectedData = useMemo(() => {
    return (watchedValue || []).filter(
      (item) => (item.selected && !item.merged) === true,
    );
  }, [watchedValue]);

  const data = useMemo(() => {
    return tab === 'selected' ? selectedData : filteredData;
  }, [tab, selectedData, filteredData]);

  const table = useReactTable({
    data: data || [],
    columns: tab === 'selected' ? selectedColumns : columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const contourList = useMemo(() => {
    return landById?.contours
      ? landById?.contours?.map((item) => {
          if (item.merged_details) {
            return { merged: true, selected: true, ...item?.merged_details };
          } else {
            return {
              merged: false,
              selected: false,
              ...item?.land_details,
              land_type: '1',
              land_extra_irragation:
                item?.land_details?.land_extra_irragation ?? '1',
              fond_type_id: item?.land_details?.fond_type_id ?? '1',
              plant_type_id: item?.land_details?.plant_type_id ?? '1',
            };
          }
        })
      : [];
  }, [landById]);
  useEffect(() => {
    form.reset({
      value: contourList as TLandContourDetail[] | [],
    });
  }, [contourList]);

  const onSubmit = async () => {
    const rawValues = form.getValues();
    const selectedContours = rawValues.value?.filter(
      (item: any) => item.selected && !item.merged,
    );

    const result = landFormSchema.safeParse({
      crop: rawValues.crop,
      value: selectedContours,
    });

    if (!result.success) {
      toast.error(t('not.available.error'));
      return;
    }

    const values = result.data;
    const payload = {
      plant_id: Number(values.crop),
      parent_land_id: Number(landById?.id) || null,
      sub_plant_id: null,
      selected_contours: (values.value || [])
        .filter((el) => el.selected)
        .map((item, i) => ({
          contour_id: item.contour_id,
          fond_type_id: item.fond_type_id,
          land_extra_irragation: item.land_extra_irragation,
          land_type: item.land_type,
          massiv_id: item.massiv_id,
          plant_type_id: item.plant_type_id,
          key: i,
        })),
    };

    try {
      await fetchLandProjects(payload);
      await fetchLandById({ id: String(id) });
      await setTab('all');
      await form.reset();
      await toast.success(t('create.project.success'));
    } catch (error) {
      console.error('Error creating document:', error);
    }
  };

  return {
    tab,
    form,
    open,
    fields,
    table,
    options,
    plants,
    landById,
    landByIdLoading,
    contourList,
    setTab,
    setOpen,
    onSubmit,
  };
}
